import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Copy, Trash2, Sparkles, RefreshCw, Wrench, ScanSearch,
  Eye, Undo2, X, Wand2,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

// Highlight words present in `after` but not in `before` (i.e. AI changes) in red.
function RedDiff({ before, after }) {
  const counts = {};
  (before.match(/\S+/g) || []).forEach((w) => { counts[w] = (counts[w] || 0) + 1; });
  const capped = after.length > 40000;
  const text = capped ? after.slice(0, 40000) : after;
  const tokens = text.split(/(\s+)/);
  return (
    <div dir="rtl" className="leading-8 text-[15px] whitespace-pre-wrap">
      {tokens.map((tok, i) => {
        if (tok === "" || /^\s+$/.test(tok)) return tok;
        if (counts[tok] > 0) { counts[tok]--; return <span key={i}>{tok}</span>; }
        return (
          <mark key={i} style={{ background: "#fecaca", color: "#991b1b", borderRadius: 3, padding: "0 2px" }}>
            {tok}
          </mark>
        );
      })}
      {capped && <div className="mt-4 text-xs text-slate-400">— عُرض أول 40,000 حرف فقط —</div>}
    </div>
  );
}

export default function AdminTools() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [dupGroups, setDupGroups] = useState(null);
  const [dupScanning, setDupScanning] = useState(false);
  const [selected, setSelected] = useState({});
  const [deleting, setDeleting] = useState(false);

  const [garbled, setGarbled] = useState(null);
  const [garbScanning, setGarbScanning] = useState(false);
  const [recovering, setRecovering] = useState({});

  const [recovered, setRecovered] = useState([]);
  const [undoing, setUndoing] = useState({});
  const [diff, setDiff] = useState({ open: false, loading: false, title: "", before: "", after: "" });

  const scanDups = useCallback(async () => {
    setDupScanning(true);
    try {
      const r = await api.get("/admin/laws/dedup-scan");
      setDupGroups(r.data?.groups || []);
      const sel = {};
      (r.data?.groups || []).forEach((g) => g.dups.forEach((d) => { sel[d.id] = true; }));
      setSelected(sel);
    } catch { toast.error(isAr ? "تعذّر الفحص" : "Scan failed"); }
    finally { setDupScanning(false); }
  }, [isAr]);

  const applyDedup = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) { toast.error(isAr ? "لم تحدّد أي نسخة" : "Nothing selected"); return; }
    if (!window.confirm(isAr ? `حذف ${ids.length} نسخة مكررة؟` : `Delete ${ids.length} duplicates?`)) return;
    setDeleting(true);
    try {
      const r = await api.post("/admin/laws/dedup-apply", { ids });
      toast.success(isAr ? `تم حذف ${r.data?.deleted} نسخة` : `Deleted ${r.data?.deleted}`);
      scanDups();
    } catch { toast.error(isAr ? "فشل الحذف" : "Delete failed"); }
    finally { setDeleting(false); }
  };

  const scanGarbled = useCallback(async () => {
    setGarbScanning(true);
    try {
      const r = await api.get("/admin/laws/garbled-scan");
      setGarbled(r.data?.items || []);
    } catch { toast.error(isAr ? "تعذّر الفحص" : "Scan failed"); }
    finally { setGarbScanning(false); }
  }, [isAr]);

  const loadRecovered = useCallback(async () => {
    try {
      const r = await api.get("/admin/laws/recovered-list");
      setRecovered(r.data?.items || []);
    } catch { /* ignore */ }
  }, []);

  const recover = async (law) => {
    setRecovering((s) => ({ ...s, [law.id]: true }));
    try {
      await api.post(`/admin/laws/${law.id}/ai-recover`);
      toast.success(isAr ? "بدأ التصحيح — حدّث القائمة بعد دقيقة" : "Recovery started — refresh in a minute");
    } catch {
      toast.error(isAr ? "فشل التصحيح" : "Recovery failed");
      setRecovering((s) => ({ ...s, [law.id]: false }));
    }
  };

  const recoverAll = async () => {
    const list = garbled || [];
    if (!list.length) return;
    if (!window.confirm(isAr ? `بدء التصحيح الشامل لـ ${list.length} وثيقة؟ قد يستغرق عدة دقائق.` : `Recover all ${list.length} documents?`)) return;
    for (const law of list) {
      setRecovering((s) => ({ ...s, [law.id]: true }));
      try { await api.post(`/admin/laws/${law.id}/ai-recover`); } catch { /* continue */ }
    }
    toast.success(isAr ? "بدأ التدقيق الشامل في الخلفية" : "Comprehensive audit started in background");
  };

  const openDiff = async (law) => {
    setDiff({ open: true, loading: true, title: law.title, before: "", after: "" });
    try {
      const r = await api.get(`/admin/laws/${law.id}/recover-diff`);
      setDiff({ open: true, loading: false, title: r.data?.title || law.title,
        before: r.data?.before || "", after: r.data?.after || "" });
    } catch {
      toast.error(isAr ? "تعذّر تحميل الفرق" : "Could not load diff");
      setDiff({ open: false, loading: false, title: "", before: "", after: "" });
    }
  };

  const undo = async (law) => {
    if (!window.confirm(isAr ? "إرجاع النص الأصلي قبل التصحيح؟" : "Restore original text?")) return;
    setUndoing((s) => ({ ...s, [law.id]: true }));
    try {
      await api.post(`/admin/laws/${law.id}/recover-undo`);
      toast.success(isAr ? "تمت إعادة النص الأصلي" : "Original restored");
      loadRecovered(); scanGarbled();
    } catch { toast.error(isAr ? "فشل التراجع" : "Undo failed"); }
    finally { setUndoing((s) => ({ ...s, [law.id]: false })); }
  };

  useEffect(() => {
    if (user?.role === "admin") { scanDups(); scanGarbled(); loadRecovered(); }
  }, [user, scanDups, scanGarbled, loadRecovered]);

  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/tools" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const selCount = Object.keys(selected).filter((k) => selected[k]).length;

  return (
    <div data-testid="admin-tools" dir={isAr ? "rtl" : "ltr"} className="space-y-10">
      <div className="flex items-center gap-2">
        <Wrench className="w-5 h-5 text-slate-700" />
        <h1 className="font-display text-2xl text-slate-900 dark:text-slate-100">
          {isAr ? "أدوات المكتبة" : "Library Tools"}
        </h1>
      </div>

      {/* ---------- DEDUP ---------- */}
      <section className="border rounded-sm p-5" style={{ borderColor: "var(--line, #e2e8f0)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Copy className="w-4 h-4 text-slate-600" />
            <h2 className="font-semibold text-slate-800 dark:text-slate-100">{isAr ? "فحص القوانين المكررة" : "Duplicate laws"}</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={scanDups} disabled={dupScanning} data-testid="dedup-scan-btn">
              {dupScanning ? <Loader2 className="w-4 h-4 me-1 animate-spin" /> : <ScanSearch className="w-4 h-4 me-1" />}
              {isAr ? "فحص" : "Scan"}
            </Button>
            {dupGroups && dupGroups.length > 0 && (
              <Button size="sm" onClick={applyDedup} disabled={deleting || selCount === 0}
                data-testid="dedup-apply-btn" className="bg-rose-600 hover:bg-rose-700 text-white">
                {deleting ? <Loader2 className="w-4 h-4 me-1 animate-spin" /> : <Trash2 className="w-4 h-4 me-1" />}
                {isAr ? `حذف المحدد (${selCount})` : `Delete selected (${selCount})`}
              </Button>
            )}
          </div>
        </div>
        {dupScanning && !dupGroups ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> {isAr ? "جارٍ الفحص…" : "Scanning…"}
          </div>
        ) : dupGroups && dupGroups.length === 0 ? (
          <div data-testid="dedup-empty" className="text-center text-emerald-700 bg-emerald-50 text-sm py-6 rounded-sm">
            {isAr ? "لا توجد قوانين مكررة. المكتبة نظيفة ✓" : "No duplicates found ✓"}
          </div>
        ) : (
          <div className="space-y-4">
            {(dupGroups || []).map((g) => (
              <div key={g.key} data-testid="dedup-group" className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--line, #e2e8f0)" }}>
                <div className="px-4 py-2 bg-emerald-50 text-emerald-800 text-xs flex items-center justify-between">
                  <span className="truncate">✓ {isAr ? "يُحتفظ به" : "Keep"}: {g.keep.title}</span>
                  <span className="shrink-0 tabular-nums">{g.keep.char_count} {isAr ? "حرف" : "chars"}</span>
                </div>
                {g.dups.map((d) => (
                  <label key={d.id} className="flex items-center gap-3 px-4 py-2.5 border-t text-sm cursor-pointer hover:bg-rose-50" style={{ borderColor: "var(--line, #e2e8f0)" }}>
                    <input type="checkbox" checked={!!selected[d.id]} onChange={(e) => setSelected((s) => ({ ...s, [d.id]: e.target.checked }))} className="w-4 h-4 accent-rose-600" />
                    <span className="flex-1 truncate text-slate-700">{d.title}</span>
                    <span className="shrink-0 tabular-nums text-slate-400">{d.char_count}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---------- AI RECOVERY (garbled) ---------- */}
      <section className="border rounded-sm p-5" style={{ borderColor: "var(--line, #e2e8f0)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: "var(--gold-deep, #b08d2f)" }} />
            <h2 className="font-semibold text-slate-800 dark:text-slate-100">{isAr ? "إصلاح الوثائق المشوّهة بالذكاء الاصطناعي" : "AI recovery of garbled documents"}</h2>
          </div>
          <div className="flex items-center gap-2">
            {garbled && garbled.length > 0 && (
              <Button size="sm" onClick={recoverAll} data-testid="recover-all-btn" style={{ background: "var(--gold-deep, #b08d2f)", color: "#fff" }}>
                <Wand2 className="w-4 h-4 me-1" /> {isAr ? `تدقيق شامل (${garbled.length})` : `Recover all (${garbled.length})`}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={scanGarbled} disabled={garbScanning} data-testid="garbled-scan-btn">
              {garbScanning ? <Loader2 className="w-4 h-4 me-1 animate-spin" /> : <RefreshCw className="w-4 h-4 me-1" />}
              {isAr ? "فحص" : "Scan"}
            </Button>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          {isAr ? "يصلح نص المسح الضوئي المشوّه بالذكاء الاصطناعي دون تغيير المعنى القانوني. النسخة الأصلية محفوظة ويمكن التراجع." : "Repairs garbled OCR text via AI without changing legal meaning. Original is backed up and reversible."}
        </p>
        {garbScanning && !garbled ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> {isAr ? "جارٍ الفحص…" : "Scanning…"}</div>
        ) : garbled && garbled.length === 0 ? (
          <div data-testid="garbled-empty" className="text-center text-emerald-700 bg-emerald-50 text-sm py-6 rounded-sm">{isAr ? "لا توجد وثائق مشوّهة ✓" : "No garbled documents ✓"}</div>
        ) : (
          <div className="space-y-2">
            {(garbled || []).map((law) => (
              <div key={law.id} data-testid={`garbled-${law.id}`} className="flex items-center gap-3 border rounded-sm px-4 py-2.5 text-sm" style={{ borderColor: "var(--line, #e2e8f0)" }}>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-slate-800 dark:text-slate-200">{law.title}</div>
                  <div className="text-xs text-slate-400 tabular-nums">{isAr ? "نسبة العربية" : "Arabic"} {(law.arabic_ratio * 100).toFixed(0)}% · {isAr ? "تشويه" : "noise"} {law.confusion_hits || 0}</div>
                </div>
                <Button size="sm" onClick={() => recover(law)} disabled={!!recovering[law.id]} data-testid={`recover-btn-${law.id}`} style={{ background: "var(--gold-deep, #b08d2f)", color: "#fff" }}>
                  {recovering[law.id] ? <><Loader2 className="w-4 h-4 me-1 animate-spin" /> {isAr ? "جارٍ…" : "…"}</> : <><Sparkles className="w-4 h-4 me-1" /> {isAr ? "تصحيح" : "Recover"}</>}
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---------- REVIEW recovered ---------- */}
      <section data-testid="recovered-section" className="border rounded-sm p-5" style={{ borderColor: "var(--line, #e2e8f0)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-slate-600" />
            <h2 className="font-semibold text-slate-800 dark:text-slate-100">{isAr ? "مراجعة التصحيحات (قبل/بعد)" : "Review corrections (before/after)"}</h2>
          </div>
          <Button variant="outline" size="sm" onClick={loadRecovered}><RefreshCw className="w-4 h-4 me-1" /> {isAr ? "تحديث" : "Refresh"}</Button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          {isAr ? "راجع ما غيّره الذكاء الاصطناعي (مميّزاً بالأحمر)، أو تراجع لإعادة النص الأصلي." : "Review what the AI changed (highlighted in red), or undo to restore the original."}
        </p>
        {recovered.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-6">{isAr ? "لا توجد تصحيحات بعد." : "No corrections yet."}</div>
        ) : (
          <div className="space-y-2">
            {recovered.map((law) => (
              <div key={law.id} data-testid={`recovered-${law.id}`} className="flex items-center gap-3 border rounded-sm px-4 py-2.5 text-sm" style={{ borderColor: "var(--line, #e2e8f0)" }}>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-slate-800 dark:text-slate-200">{law.title}</div>
                  <div className="text-xs text-slate-400 tabular-nums">
                    {law.before_chars} → {law.char_count} {isAr ? "حرف" : "chars"}
                    {law.text_quality === "ai_recovered" && <span className="ms-2 text-emerald-600">✓ {isAr ? "مُصحَّح" : "recovered"}</span>}
                    {law.text_quality === "original" && <span className="ms-2 text-slate-500">↩ {isAr ? "أُرجع للأصل" : "restored"}</span>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => openDiff(law)} data-testid={`review-btn-${law.id}`}>
                  <Eye className="w-4 h-4 me-1" /> {isAr ? "مراجعة" : "Review"}
                </Button>
                {law.text_quality === "ai_recovered" && (
                  <Button size="sm" variant="outline" onClick={() => undo(law)} disabled={!!undoing[law.id]} data-testid={`undo-btn-${law.id}`} className="text-rose-700 border-rose-300 hover:bg-rose-50">
                    {undoing[law.id] ? <Loader2 className="w-4 h-4 me-1 animate-spin" /> : <Undo2 className="w-4 h-4 me-1" />} {isAr ? "تراجع" : "Undo"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---------- DIFF MODAL ---------- */}
      {diff.open && (
        <div data-testid="diff-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setDiff((d) => ({ ...d, open: false }))}>
          <div className="bg-white dark:bg-slate-900 rounded-sm max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--line, #e2e8f0)" }}>
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 truncate">{isAr ? "التغييرات بالأحمر — " : "Changes in red — "}{diff.title}</h3>
              <button onClick={() => setDiff((d) => ({ ...d, open: false }))} data-testid="diff-close" className="p-1.5 hover:bg-slate-100 rounded-md"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-2 text-xs text-slate-500 border-b" style={{ borderColor: "var(--line, #e2e8f0)" }}>
              {isAr ? "النص بعد التصحيح — الكلمات التي أصلحها الذكاء الاصطناعي مظلّلة بالأحمر." : "Corrected text — words the AI fixed are highlighted red."}
            </div>
            <div className="p-5 overflow-auto">
              {diff.loading ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> {isAr ? "جارٍ التحميل…" : "Loading…"}</div>
              ) : (
                <RedDiff before={diff.before} after={diff.after} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
